/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsAddVersion } from "../funcs/skillsAddVersion.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { RecordSkillResult } from "../models/components/recordskillresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AddSkillVersionRequest,
  AddSkillVersionSecurity,
} from "../models/operations/addskillversion.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AddSkillVersionMutationVariables = {
  request: AddSkillVersionRequest;
  security?: AddSkillVersionSecurity | undefined;
  options?: RequestOptions;
};

export type AddSkillVersionMutationData = RecordSkillResult;

export type AddSkillVersionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * addVersion skills
 *
 * @remarks
 * Record an uploaded SKILL.md as a version of an existing skill. The implementation requires the skills product feature and skill write scope, and returns the existing canonical version as a no-op when appropriate.
 */
export function useAddSkillVersionMutation(
  options?: MutationHookOptions<
    AddSkillVersionMutationData,
    AddSkillVersionMutationError,
    AddSkillVersionMutationVariables
  >,
): UseMutationResult<
  AddSkillVersionMutationData,
  AddSkillVersionMutationError,
  AddSkillVersionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAddSkillVersionMutation(client, options),
    ...options,
  });
}

export function mutationKeyAddSkillVersion(): MutationKey {
  return ["@gram/client", "skills", "addVersion"];
}

export function buildAddSkillVersionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AddSkillVersionMutationVariables,
  ) => Promise<AddSkillVersionMutationData>;
} {
  return {
    mutationKey: mutationKeyAddSkillVersion(),
    mutationFn: function addSkillVersionMutationFn({
      request,
      security,
      options,
    }): Promise<AddSkillVersionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsAddVersion(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
